import { createSlug } from '../../../utils/helpers';
import {
  DEFAULT_SERVICE_LISTING_STYLE,
  DEFAULT_THEME_COLOR,
  SERVICE_LISTING_STYLE_OPTIONS
} from '../landingPageTheme';

export const LANDING_PAGE_TYPES = {
  CONTACT: 'contact',
  CUSTOM: 'custom'
};

export const LANDING_PAGE_SOCIAL_PLATFORMS = [
  { key: 'facebook', label: 'Facebook' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'x', label: 'X' },
  { key: 'youtube', label: 'YouTube' }
];

const RESERVED_PAGE_SLUGS = new Set([
  'service',
  'book-appointment',
  'account',
  'profile'
]);

const trimText = (value) => (typeof value === 'string' ? value.trim() : '');

const trimOptionalText = (value) => (typeof value === 'string' ? value.trim() : '');

const preserveText = (value) => (typeof value === 'string' ? value : '');
const normalizeOverlayOpacity = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 0.1;
  }

  return Math.min(0.9, Math.max(0, numericValue));
};

const createPageId = (prefix) => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const getPageDefaults = (type) => {
  if (type === LANDING_PAGE_TYPES.CONTACT) {
    return {
      id: createPageId('landing-page'),
      slug: 'contact',
      title: 'Contact',
      navLabel: 'Contact',
      type,
      isVisible: true,
      content: '',
      contactFields: {
        phone: '',
        email: '',
        address: ''
      }
    };
  }

  return {
    id: createPageId('landing-page'),
    slug: 'page',
    title: 'New Page',
    navLabel: 'New Page',
    type: LANDING_PAGE_TYPES.CUSTOM,
    isVisible: true,
    content: '',
    contactFields: {
      phone: '',
      email: '',
      address: ''
    }
  };
};

export const getDefaultLandingHomePage = (companyData = {}) => {
  return {
    featuredSectionTitle: 'Featured services',
    featuredSectionDescription: 'Explore the latest services, pricing, and appointment options from one polished storefront.'
  };
};

export const normalizeLandingPageSlug = (value, fallbackValue = 'page') => {
  const baseSlug = createSlug(trimText(value) || trimText(fallbackValue) || 'page');
  const slug = baseSlug || 'page';

  if (RESERVED_PAGE_SLUGS.has(slug)) {
    return `${slug}-page`;
  }

  return slug;
};

const normalizeContactFields = (contactFields = {}) => ({
  phone: trimOptionalText(contactFields.phone),
  email: trimOptionalText(contactFields.email),
  address: preserveText(contactFields.address)
});

const normalizeLandingSocialLinks = (socialLinks = {}) => LANDING_PAGE_SOCIAL_PLATFORMS.reduce((accumulator, platform) => ({
  ...accumulator,
  [platform.key]: trimOptionalText(socialLinks?.[platform.key])
}), {});

const normalizeCustomPage = (page = {}, index = 0) => {
  const requestedType = trimText(page.type);
  const normalizedType = requestedType === LANDING_PAGE_TYPES.CONTACT
    ? LANDING_PAGE_TYPES.CONTACT
    : LANDING_PAGE_TYPES.CUSTOM;

  const defaults = getPageDefaults(normalizedType);
  const title = trimText(page.title) || defaults.title || `Page ${index + 1}`;

  return {
    id: trimText(page.id) || defaults.id || createPageId('landing-page'),
    slug: normalizedType === LANDING_PAGE_TYPES.CUSTOM
      ? normalizeLandingPageSlug(page.slug, title)
      : defaults.slug,
    title,
    navLabel: title,
    type: normalizedType,
    isVisible: page.isVisible !== false,
    content: preserveText(page.content),
    contactFields: normalizeContactFields(page.contactFields)
  };
};

const ensureUniquePageSlugs = (pages) => {
  const slugCounts = new Map();

  return pages.map((page) => {
    const baseSlug = page.type === LANDING_PAGE_TYPES.CUSTOM
      ? normalizeLandingPageSlug(page.slug, page.title)
      : page.slug;

    let nextSlug = baseSlug;
    let attempt = slugCounts.get(baseSlug) || 0;

    while (slugCounts.has(nextSlug)) {
      attempt += 1;
      nextSlug = `${baseSlug}-${attempt + 1}`;
    }

    slugCounts.set(nextSlug, attempt);

    return {
      ...page,
      slug: nextSlug
    };
  });
};

export const createLandingCustomPage = (title = 'New Page') => {
  const resolvedTitle = trimText(title) || 'New Page';

  return normalizeCustomPage({
    id: createPageId('landing-page'),
    title: resolvedTitle,
    navLabel: resolvedTitle,
    slug: normalizeLandingPageSlug(resolvedTitle),
    type: LANDING_PAGE_TYPES.CUSTOM,
    isVisible: true,
    content: ''
  });
};

export const createLandingContactPage = () => normalizeCustomPage({
  ...getPageDefaults(LANDING_PAGE_TYPES.CONTACT),
  type: LANDING_PAGE_TYPES.CONTACT
});

export const normalizeLandingPageSettings = (landingPageSettings = {}, companyData = {}) => {
  const defaults = getDefaultLandingHomePage(companyData);
  const homePage = landingPageSettings?.homePage || {};
  const rawPages = Array.isArray(landingPageSettings?.customPages)
    ? landingPageSettings.customPages.map((page, index) => normalizeCustomPage(page, index))
    : [];

  return {
    themeColor: trimText(landingPageSettings.themeColor) || DEFAULT_THEME_COLOR,
    storefrontBackgroundImageUrl: trimOptionalText(
      landingPageSettings.storefrontBackgroundImageUrl || landingPageSettings.heroImageUrl
    ),
    storefrontBackgroundOverlayOpacity: normalizeOverlayOpacity(landingPageSettings.storefrontBackgroundOverlayOpacity),
    serviceListingStyle: landingPageSettings.serviceListingStyle === SERVICE_LISTING_STYLE_OPTIONS.COMPACT_ROWS
      ? SERVICE_LISTING_STYLE_OPTIONS.COMPACT_ROWS
      : DEFAULT_SERVICE_LISTING_STYLE,
    navigation: {
      showHome: landingPageSettings?.navigation?.showHome !== false
    },
    homePage: {
      featuredSectionTitle: trimText(homePage.featuredSectionTitle) || defaults.featuredSectionTitle,
      featuredSectionDescription: trimText(homePage.featuredSectionDescription) || defaults.featuredSectionDescription
    },
    socialLinks: normalizeLandingSocialLinks(landingPageSettings.socialLinks),
    customPages: ensureUniquePageSlugs(rawPages)
  };
};

export const sanitizeLandingPageSettingsForSave = (landingPageSettings = {}, companyData = {}) => {
  const normalizedSettings = normalizeLandingPageSettings(landingPageSettings, companyData);
  const customPages = ensureUniquePageSlugs(
    normalizedSettings.customPages.map((page, index) => normalizeCustomPage(page, index))
  );

  return {
    themeColor: trimText(normalizedSettings.themeColor) || DEFAULT_THEME_COLOR,
    storefrontBackgroundImageUrl: trimOptionalText(normalizedSettings.storefrontBackgroundImageUrl),
    storefrontBackgroundOverlayOpacity: normalizeOverlayOpacity(normalizedSettings.storefrontBackgroundOverlayOpacity),
    serviceListingStyle: normalizedSettings.serviceListingStyle,
    navigation: {
      showHome: normalizedSettings.navigation?.showHome !== false
    },
    homePage: {
      featuredSectionTitle: trimOptionalText(normalizedSettings.homePage.featuredSectionTitle),
      featuredSectionDescription: trimOptionalText(normalizedSettings.homePage.featuredSectionDescription)
    },
    socialLinks: normalizeLandingSocialLinks(normalizedSettings.socialLinks),
    customPages
  };
};

export const getLandingNavigationPages = (landingPageSettings = {}, companyData = {}) => {
  const normalizedSettings = normalizeLandingPageSettings(landingPageSettings, companyData);

  return normalizedSettings.customPages.filter((page) => page.isVisible !== false);
};

export const getLandingPageBySlug = (landingPageSettings = {}, slug, companyData = {}) => {
  if (!trimText(slug)) {
    return null;
  }

  const normalizedSettings = normalizeLandingPageSettings(landingPageSettings, companyData);

  return normalizedSettings.customPages.find((page) => page.slug === slug) || null;
};

export const getLandingPageByType = (landingPageSettings = {}, type, companyData = {}) => {
  const normalizedSettings = normalizeLandingPageSettings(landingPageSettings, companyData);
  return normalizedSettings.customPages.find((page) => page.type === type) || null;
};

export const isLandingPageSlugReserved = (slug) => RESERVED_PAGE_SLUGS.has(trimText(slug));
